import { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import "./Logbooks.css";

export default function ReagentInventoryLogs() {
  const baseURL = process.env.REACT_APP_BASE_URL;
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [employees, setEmployees] = useState([]);

  const [formData, setFormData] = useState({
    receiving_date: "",
    items_particulars: "",
    batch_lot_number: "",
    expiry_date: "",
    quantity_received: "",
    opening_date: "",
    stock_in_hand: "",
    stock_consumed: "",
    balance_stock: "",
    storage_condition: "",
    analyst_id: "",
    remarks: "",
  });

  const token = localStorage.getItem("token");

  // ✅ Axios instance with token
  const api = axios.create({
   baseURL: `${baseURL}`,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  // ✅ Handle token expiry
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        toast.error("Session expired. Redirecting to login...");
        localStorage.removeItem("token");
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }
  );

  // ✅ Fetch all logs and employees
  useEffect(() => {
    fetchLogs();
    fetchEmployees();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await api.get("/reagent_inventory_logs");
      setLogs(response.data);
    } catch (error) {
      console.error("Error fetching reagent inventory logs:", error);
      alert("Error fetching reagent logs. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get("/employees");
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // ✅ Add new reagent log
  const handleAdd = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        quantity_received: parseInt(formData.quantity_received) || 0,
        stock_in_hand: parseInt(formData.stock_in_hand) || 0,
        stock_consumed: parseInt(formData.stock_consumed) || 0,
        balance_stock: parseInt(formData.balance_stock) || 0,
        analyst_id: parseInt(formData.analyst_id),
      };

      await api.post("/reagent_inventory_logs", payload);
      setShowForm(false);
      setFormData({
        receiving_date: "",
        items_particulars: "",
        batch_lot_number: "",
        expiry_date: "",
        quantity_received: "",
        opening_date: "",
        stock_in_hand: "",
        stock_consumed: "",
        balance_stock: "",
        storage_condition: "",
        analyst_id: "",
        remarks: "",
      });
      fetchLogs();
      toast.success("Reagent inventory log added successfully!");
    } catch (error) {
      console.error("Error adding reagent inventory log:", error);
      alert("Failed to add reagent inventory log entry. Check console for details.");
    }
  };

  // ✅ Export to Excel
  const exportToExcel = () => {
    if (logs.length === 0) {
      alert("No data available to export.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(logs);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ReagentInventoryLogs");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "ReagentInventoryLogs.xlsx");
  };

  return (
    <div className="logbooks-layout">
      <div className="logbooks-container">
        <div className="logbooks-header">
          <h2>Reagent Inventory Logs</h2>
          <div className="header-actions">
            <button className="export-btn" onClick={exportToExcel}>
              Export to Excel
            </button>
            <button className="add-btn" onClick={() => setShowForm(true)}>
              + Add
            </button>
          </div>
        </div>

        {loading ? (
          <p>Loading reagent inventory logs...</p>
        ) : (
          <table className="logbooks-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Receiving Date</th>
                <th>Item Particulars</th>
                <th>Batch/Lot No.</th>
                <th>Expiry Date</th>
                <th>Qty Received</th>
                <th>Opening Date</th>
                <th>Stock In Hand</th>
                <th>Stock Consumed</th>
                <th>Balance Stock</th>
                <th>Storage Condition</th>
                <th>Analyst</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {logs.length > 0 ? (
                logs.map((log) => (
                  <tr key={log.id}>
                    <td>{log.id}</td>
                    <td>{log.receiving_date}</td>
                    <td>{log.items_particulars}</td>
                    <td>{log.batch_lot_number}</td>
                    <td>{log.expiry_date}</td>
                    <td>{log.quantity_received}</td>
                    <td>{log.opening_date}</td>
                    <td>{log.stock_in_hand}</td>
                    <td>{log.stock_consumed}</td>
                    <td>{log.balance_stock}</td>
                    <td>{log.storage_condition}</td>
                    <td>{log.analyst_name}</td>
                    <td>{log.remarks}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="13" style={{ textAlign: "center" }}>
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {/* ✅ Add Modal Form */}
        {showForm && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Add Reagent Inventory Log</h3>
              <form onSubmit={handleAdd}>
                <label htmlFor="opening_date">Receiving Date</label>
                <input
                  type="date"
                  name="receiving_date"
                  placeholder="Receiving Date"
                  value={formData.receiving_date}
                  onChange={handleChange}
                  required
                />

                <input
                  type="text"
                  name="items_particulars"
                  placeholder="Items Particulars"
                  value={formData.items_particulars}
                  onChange={handleChange}
                  required
                />

                <input
                  type="text"
                  name="batch_lot_number"
                  placeholder="Batch/Lot Number"
                  value={formData.batch_lot_number}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="expiry_date">Expiry Date</label>
                <input
                  type="date"
                  name="expiry_date"
                  placeholder="Expiry Date"
                  value={formData.expiry_date}
                  onChange={handleChange}
                  required
                />

                <input
                  type="number"
                  name="quantity_received"
                  placeholder="Quantity Received"
                  value={formData.quantity_received}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="opening_date">Opening Date</label>
                <input
                  type="date"
                  name="opening_date"
                  placeholder="Opening Date"
                  value={formData.opening_date}
                  onChange={handleChange}
                  required
                />

                <input
                  type="number"
                  name="stock_in_hand"
                  placeholder="Stock In Hand"
                  value={formData.stock_in_hand}
                  onChange={handleChange}
                  required
                />

                <input
                  type="number"
                  name="stock_consumed"
                  placeholder="Stock Consumed"
                  value={formData.stock_consumed}
                  onChange={handleChange}
                  required
                />

                <input
                  type="number"
                  name="balance_stock"
                  placeholder="Balance Stock"
                  value={formData.balance_stock}
                  onChange={handleChange}
                  required
                />

                <select
                  name="storage_condition"
                  value={formData.storage_condition}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Storage Condition</option>
                  <option value="Ambient">Ambient</option>
                  <option value="Refrigerated">Refrigerated</option>
                  <option value="Frozen">Frozen</option>
                </select>

                <select
                  name="analyst_id"
                  value={formData.analyst_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Analyst</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.comp_name}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  name="remarks"
                  placeholder="Remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                />

                <div className="modal-actions">
                  <button type="submit" className="save-btn">
                    Save
                  </button>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => setShowForm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}